import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  FileText, Upload, Download, Trash2, Search, Calendar, AlertTriangle,
  CheckCircle, Clock, Eye, Plus, Filter, RefreshCw, File, FileImage,
  FileSpreadsheet, FileArchive
} from 'lucide-react';
import { toast } from 'sonner';

const TIPOS_DOCUMENTO = [
  'Estatutos',
  'Regulamento Interno',
  'Relatório de Atividades',
  'Plano de Atividades',
  'Relatório de Contas',
  'Balanço',
  'Demonstração de Resultados',
  'Certificado de Registo',
  'Acordo de Cooperação',
  'Alvará/Licença',
  'Seguro',
  'Declaração IRS/IRC',
  'Certidão Permanente',
  'Outro'
];

const STATUS_CONFIG = {
  'Pendente': { color: 'bg-amber-500', icon: Clock },
  'Aprovado': { color: 'bg-green-500', icon: CheckCircle },
  'Rejeitado': { color: 'bg-red-500', icon: AlertTriangle },
  'A Expirar': { color: 'bg-orange-500', icon: AlertTriangle }
};

const getFileIcon = (fileName) => {
  const ext = fileName?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return FileText;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif': return FileImage;
    case 'xlsx':
    case 'xls':
    case 'csv': return FileSpreadsheet;
    case 'zip':
    case 'rar': return FileArchive;
    default: return File;
  }
};

export default function PortalDocumentosPage() {
  const [loading, setLoading] = useState(true);
  const [documentos, setDocumentos] = useState([]);
  const [filteredDocs, setFilteredDocs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('all');
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [documentosAviso, setDocumentosAviso] = useState([]);
  
  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTipo, setUploadTipo] = useState('');
  const [uploadDescricao, setUploadDescricao] = useState('');
  const [uploadValidade, setUploadValidade] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    validos: 0,
    pendentes: 0,
    a_expirar: 0,
    expirados: 0
  });

  useEffect(() => {
    loadDocumentos();
  }, []);

  useEffect(() => {
    filterDocumentos();
  }, [documentos, searchTerm, filtroTipo, filtroStatus]);

  const loadDocumentos = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const userStr = localStorage.getItem('portal_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const instituicaoId = user.instituicao_id;
      
      if (!instituicaoId) {
        toast.error('Instituição não encontrada');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/documentos`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        const docs = data.documentos || data || [];
        setDocumentos(docs);
        
        // Calculate stats
        const today = new Date();
        const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
        
        let validos = 0;
        let pendentes = 0;
        let a_expirar = 0;
        let expirados = 0;
        
        docs.forEach(doc => {
          if (doc.status_validacao === 'Pendente') pendentes++;
          else if (doc.status_validacao === 'Aprovado') validos++;
          
          if (doc.data_validade) {
            const validade = new Date(doc.data_validade);
            if (validade < today) expirados++;
            else if (validade < thirtyDaysFromNow) a_expirar++;
          }
        });
        
        setStats({
          total: docs.length,
          validos,
          pendentes,
          a_expirar,
          expirados
        });
      }

      // Carregar documentos em aviso
      try {
        const avisoRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/portal/documentos-aviso`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (avisoRes.ok) setDocumentosAviso(await avisoRes.json());
      } catch (e) {
        console.error('Erro ao carregar avisos:', e);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const filterDocumentos = () => {
    let filtered = [...documentos];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.nome_original?.toLowerCase().includes(search) ||
        d.tipo_documento?.toLowerCase().includes(search)
      );
    }
    
    if (filtroTipo !== 'all') {
      filtered = filtered.filter(d => d.tipo_documento === filtroTipo);
    }
    
    if (filtroStatus !== 'all') {
      filtered = filtered.filter(d => d.status_validacao === filtroStatus);
    }
    
    setFilteredDocs(filtered);
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadTipo) {
      toast.error('Selecione um ficheiro e o tipo de documento');
      return;
    }
    
    setUploading(true);
    try {
      const token = localStorage.getItem('portal_token');
      const userStr = localStorage.getItem('portal_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const instituicaoId = user.instituicao_id;
      
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('instituicao_id', instituicaoId);
      formData.append('tipo_documento', uploadTipo);
      formData.append('descricao', uploadDescricao);
      if (uploadValidade) {
        formData.append('data_validade', uploadValidade);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/documentos/upload`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );
      
      if (response.ok) {
        toast.success('Documento enviado com sucesso');
        setUploadModalOpen(false);
        setUploadFile(null);
        setUploadTipo('');
        setUploadDescricao('');
        setUploadValidade('');
        loadDocumentos();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao enviar documento');
      }
    } catch (error) {
      toast.error('Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/documentos/${doc.id}/download`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.nome_original || 'documento';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        toast.error('Erro ao descarregar documento');
      }
    } catch (error) {
      toast.error('Erro ao descarregar documento');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Tem a certeza que deseja eliminar este documento?')) return;
    
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/portal/documentos/${docId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Documento eliminado');
        loadDocumentos();
      } else {
        toast.error('Erro ao eliminar documento');
      }
    } catch (error) {
      toast.error('Erro ao eliminar documento');
    }
  };

  const getDocumentStatus = (doc) => {
    if (doc.data_validade) {
      const today = new Date();
      const validade = new Date(doc.data_validade);
      const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      
      if (validade < today) return 'Expirado';
      if (validade < thirtyDaysFromNow) return 'A Expirar';
    }
    return doc.status_validacao || 'Pendente';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="portal-documentos-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-documentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Documentos</h1>
          <p className="text-slate-500">Gerir documentos da instituição</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadDocumentos}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setUploadModalOpen(true)} className="bg-[#0066CC] hover:bg-[#0055AA]">
            <Upload className="w-4 h-4 mr-2" />
            Enviar Documento
          </Button>
        </div>
      </div>

      {/* Banner de Alertas de Documentos */}
      {documentosAviso.length > 0 && (
        <div
          data-testid="documentos-aviso-banner-portal"
          className={`border rounded-lg p-4 ${
            documentosAviso.some(d => d.urgencia === 'expirado')
              ? 'bg-red-50 border-red-300'
              : documentosAviso.some(d => d.urgencia === 'critico')
              ? 'bg-orange-50 border-orange-300'
              : 'bg-amber-50 border-amber-200'
          }`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${
              documentosAviso.some(d => d.urgencia === 'expirado') ? 'text-red-600'
              : documentosAviso.some(d => d.urgencia === 'critico') ? 'text-orange-600'
              : 'text-amber-600'
            }`} />
            <div className="flex-1">
              <p className={`font-semibold ${
                documentosAviso.some(d => d.urgencia === 'expirado') ? 'text-red-800'
                : documentosAviso.some(d => d.urgencia === 'critico') ? 'text-orange-800'
                : 'text-amber-800'
              }`}>
                {documentosAviso.filter(d => d.urgencia === 'expirado').length > 0
                  ? `${documentosAviso.filter(d => d.urgencia === 'expirado').length} documento(s) expirado(s) — ação imediata necessária`
                  : `${documentosAviso.length} documento(s) com prazo próximo`}
              </p>
              <ul className="mt-2 space-y-1">
                {documentosAviso.slice(0, 5).map((doc) => (
                  <li key={doc.id} className="flex items-start gap-2 text-sm">
                    <span className={`mt-0.5 shrink-0 font-bold ${
                      doc.urgencia === 'expirado' ? 'text-red-500' : doc.urgencia === 'critico' ? 'text-orange-500' : 'text-amber-500'
                    }`}>•</span>
                    <span className={doc.urgencia === 'expirado' ? 'text-red-700' : doc.urgencia === 'critico' ? 'text-orange-700' : 'text-amber-700'}>
                      <strong>{doc.nome}</strong>
                      {doc.urgencia === 'expirado'
                        ? ` — expirado há ${Math.abs(doc.dias_restantes)} dia(s)`
                        : ` — expira em ${doc.dias_restantes} dia(s) (${new Date(doc.data_limite).toLocaleDateString('pt-PT')})`}
                      {doc.tipo_aviso === 'pendente' && <span className="text-xs ml-1 opacity-70">[pendente de entrega]</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats.validos}</p>
              <p className="text-sm text-slate-500">Aprovados</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.pendentes}</p>
              <p className="text-sm text-slate-500">Pendentes</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.a_expirar}</p>
              <p className="text-sm text-slate-500">A Expirar</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{stats.expirados}</p>
              <p className="text-sm text-slate-500">Expirados</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Pesquisar documentos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-documentos"
              />
            </div>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Tipo de documento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                {TIPOS_DOCUMENTO.map(tipo => (
                  <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="Aprovado">Aprovado</SelectItem>
                <SelectItem value="Pendente">Pendente</SelectItem>
                <SelectItem value="Rejeitado">Rejeitado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Documentos</CardTitle>
          <CardDescription>{filteredDocs.length} documento(s) encontrado(s)</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredDocs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">Nenhum documento encontrado</p>
              <Button onClick={() => setUploadModalOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Enviar Primeiro Documento
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 dark:bg-slate-800">
                  <TableHead>Documento</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Data Upload</TableHead>
                  <TableHead>Validade</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDocs.map((doc) => {
                  const FileIcon = getFileIcon(doc.nome_original);
                  const status = getDocumentStatus(doc);
                  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG['Pendente'];
                  
                  return (
                    <TableRow key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                            <FileIcon className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{doc.nome_original}</p>
                            {doc.descricao && (
                              <p className="text-xs text-slate-500 truncate max-w-[200px]">{doc.descricao}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{doc.tipo_documento || '-'}</span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {doc.data_upload ? new Date(doc.data_upload).toLocaleDateString('pt-PT') : '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {doc.data_validade ? (() => {
                          const hoje = new Date();
                          const validade = new Date(doc.data_validade);
                          const dias = Math.round((validade - hoje) / (1000 * 60 * 60 * 24));
                          return (
                            <div className="flex items-center gap-2">
                              <span className={`${dias < 0 ? 'text-red-600 font-semibold' : dias <= 7 ? 'text-orange-600 font-semibold' : dias <= 30 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {validade.toLocaleDateString('pt-PT')}
                              </span>
                              {dias < 0 && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-300">
                                  <AlertTriangle className="w-2.5 h-2.5" />EXPIRADO
                                </span>
                              )}
                              {dias >= 0 && dias <= 7 && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 border border-orange-300">
                                  <AlertTriangle className="w-2.5 h-2.5" />{dias}d
                                </span>
                              )}
                              {dias > 7 && dias <= 30 && (
                                <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  <Clock className="w-2.5 h-2.5" />{dias}d
                                </span>
                              )}
                            </div>
                          );
                        })() : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig.color} text-white`}>
                          {status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(doc.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#0066CC]" />
              Enviar Documento
            </DialogTitle>
            <DialogDescription>
              Selecione o ficheiro e preencha os dados
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                Ficheiro *
              </label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center hover:border-[#0066CC] transition-colors">
                <input
                  type="file"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.zip"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {uploadFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <FileText className="w-6 h-6 text-[#0066CC]" />
                      <span className="text-sm font-medium">{uploadFile.name}</span>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-500">Clique para selecionar</p>
                      <p className="text-xs text-slate-400 mt-1">PDF, DOC, XLS, JPG, PNG (max. 10MB)</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                Tipo de Documento *
              </label>
              <Select value={uploadTipo} onValueChange={setUploadTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_DOCUMENTO.map(tipo => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                Data de Validade
              </label>
              <Input
                type="date"
                value={uploadValidade}
                onChange={(e) => setUploadValidade(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
                Descrição
              </label>
              <Input
                placeholder="Descrição opcional..."
                value={uploadDescricao}
                onChange={(e) => setUploadDescricao(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !uploadFile || !uploadTipo}
              className="bg-[#0066CC] hover:bg-[#0055AA]"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  A enviar...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
